import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { admin, auth } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Trash2, ShieldCheck, UserPlus, KeyRound, Database, Users, Workflow, Wrench, Server, CheckCircle2, Mail, Zap } from "lucide-react";
import AutoApproveRulesPanel from "@/components/AutoApproveRulesPanel";
import DbConsole from "@/components/DbConsole";
import ApprovalPolicyPanel from "@/components/ApprovalPolicyPanel";
import MaintenancePanel from "@/components/MaintenancePanel";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";

const ROLES = ["Requestor", "Authoriser", "Approver", "Both", "Admin"];

export default function Admin() {
  const me = auth.user();
  const nav = useNavigate();
  const [logins, setLogins] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("logins");

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([admin.listLogins(), admin.candidates()]);
      setLogins(a.data || []);
      setCandidates(b.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logins");
      if (e?.response?.status === 403) nav("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const missingLogin = candidates.filter((c) => !c.has_login);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav("/")} className="text-white hover:bg-white/10" data-testid="back-to-dashboard">
              <ArrowLeft size={16} className="mr-1" /> Dashboard
            </Button>
            <div className="hidden sm:block h-6 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} />
              <div className="text-sm sm:text-base font-bold tracking-tight">Manage Logins</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <WorkspaceSwitcher role={me?.role} />
            <div className="text-xs sm:text-sm text-cyan-100 hidden md:block">
              Admin: <span className="font-semibold text-white">{me?.full_name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-5">
        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-sm p-1 inline-flex" data-testid="admin-tabs">
          <button
            onClick={() => setTab("logins")}
            data-testid="tab-logins"
            className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === "logins" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Users size={14} /> Logins &amp; Roles
          </button>
          <button
            onClick={() => setTab("db")}
            data-testid="tab-db"
            className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === "db" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Database size={14} /> DB Console
          </button>
          <button
            onClick={() => setTab("policy")}
            data-testid="tab-policy"
            className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === "policy" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Workflow size={14} /> Approval Policy
          </button>
          <button
            onClick={() => setTab("auto-approve")}
            data-testid="tab-auto-approve"
            className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === "auto-approve" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Zap size={14} /> Auto-Approve
          </button>
          <button
            onClick={() => setTab("maintenance")}
            data-testid="tab-maintenance"
            className={`px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors ${tab === "maintenance" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <Wrench size={14} /> Maintenance
          </button>
          <button
            onClick={() => nav("/admin/sap-data")}
            data-testid="tab-sap-data"
            className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors text-slate-600 hover:bg-slate-100"
          >
            <Server size={14} /> SAP Data
          </button>
          <button
            onClick={() => nav("/admin/list-prices")}
            data-testid="tab-list-prices"
            className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors text-slate-600 hover:bg-slate-100"
          >
            <KeyRound size={14} /> List Prices
          </button>
          <button
            onClick={() => nav("/admin/list-price-approvals")}
            data-testid="tab-list-price-approvals"
            className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors text-slate-600 hover:bg-slate-100"
          >
            <CheckCircle2 size={14} /> Price Approvals
          </button>
          <button
            onClick={() => nav("/admin/email-log")}
            data-testid="tab-email-log"
            className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors text-slate-600 hover:bg-slate-100"
          >
            <Mail size={14} /> Email Log
          </button>
          <button
            onClick={() => nav("/admin/buyer-assignments")}
            data-testid="tab-buyer-assignments"
            className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-1.5 transition-colors text-slate-600 hover:bg-slate-100"
          >
            <Users size={14} /> Buyer Assignments
          </button>
        </div>

        {tab === "db" && <DbConsole />}
        {tab === "policy" && <ApprovalPolicyPanel />}
        {tab === "auto-approve" && <AutoApproveRulesPanel />}
        {tab === "maintenance" && <MaintenancePanel />}

        {tab === "logins" && <>
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Active logins</div>
            <div className="text-2xl font-extrabold text-slate-900 num mt-1">{logins.length}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Approvers without login</div>
            <div className="text-2xl font-extrabold text-yellow-700 num mt-1">{missingLogin.length}</div>
          </div>
          <CreateLoginDialog candidates={candidates} onCreated={refresh} />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="text-sm font-semibold text-slate-800">Existing logins</div>
            <div className="text-xs text-slate-500">All accounts that can sign into this app.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Username</th>
                  <th className="text-left px-3 py-2">Full Name</th>
                  <th className="text-left px-3 py-2">Emp No</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2">Active</th>
                  <th className="text-right px-3 py-2 w-[160px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500">
                    <Loader2 className="inline animate-spin mr-2" size={14} /> Loading...
                  </td></tr>
                )}
                {!loading && logins.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500">No logins yet.</td></tr>
                )}
                {logins.map((l) => (
                  <LoginRow key={l.username} login={l} onUpdated={refresh} canDelete={l.username !== me?.username} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {missingLogin.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <div className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <UserPlus size={16} /> Approvers without a login
            </div>
            <div className="text-xs text-amber-800 mb-3">
              These employee IDs appear as authoriser or approver on recent PRs but have no login yet.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-amber-900 uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-1">Emp No</th>
                    <th className="text-left px-2 py-1">Name (from emp)</th>
                    <th className="text-right px-2 py-1">As Authoriser</th>
                    <th className="text-right px-2 py-1">As Approver</th>
                  </tr>
                </thead>
                <tbody>
                  {missingLogin.map((c) => (
                    <tr key={c.emp_no} className="border-t border-amber-200">
                      <td className="px-2 py-1 mono font-semibold">{c.emp_no}</td>
                      <td className="px-2 py-1">{c.name || "—"}</td>
                      <td className="px-2 py-1 text-right num">{c.authoriser_pr_count}</td>
                      <td className="px-2 py-1 text-right num">{c.approver_pr_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>}
      </main>
    </div>
  );
}

function CreateLoginDialog({ candidates, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    username: "", password: "", full_name: "", emp_no: "", role: "Approver", active: true,
  });

  const submit = async () => {
    if (!form.username || !form.password || !form.emp_no) {
      toast.error("Username, password and Emp No are required");
      return;
    }
    setBusy(true);
    try {
      await admin.createLogin(form);
      toast.success(`Login '${form.username}' created`);
      setOpen(false);
      setForm({ username: "", password: "", full_name: "", emp_no: "", role: "Approver", active: true });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const pickCandidate = (emp_no) => {
    const c = candidates.find((x) => x.emp_no === emp_no);
    setForm((f) => ({
      ...f,
      emp_no,
      full_name: c?.name || f.full_name,
      role: c && c.authoriser_pr_count > 0 && c.approver_pr_count > 0 ? "Both"
            : c && c.authoriser_pr_count > 0 ? "Authoriser"
            : "Approver",
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-cyan-700 hover:bg-cyan-600" data-testid="open-create-login">
          <Plus size={14} className="mr-1" /> New Login
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create login</DialogTitle>
          <DialogDescription>
            Adds a row to <code>ULogin</code> in MS SQL and stores the role in the app&apos;s MongoDB.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Emp No (from PR data)</Label>
            <Select value={form.emp_no} onValueChange={pickCandidate}>
              <SelectTrigger data-testid="select-empno"><SelectValue placeholder="Pick from PR authorisers/approvers..." /></SelectTrigger>
              <SelectContent>
                {candidates.filter((c) => !c.has_login).map((c) => (
                  <SelectItem key={c.emp_no} value={c.emp_no}>
                    {c.emp_no} {c.name ? `· ${c.name}` : ""}
                    {c.authoriser_pr_count > 0 ? ` · Auth: ${c.authoriser_pr_count}` : ""}
                    {c.approver_pr_count > 0 ? ` · App: ${c.approver_pr_count}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Or type a custom Emp No below if not in the list.
            </p>
            <Input
              value={form.emp_no} onChange={(e) => setForm({ ...form, emp_no: e.target.value })}
              placeholder="Emp No" className="mt-1" data-testid="input-empno"
            />
          </div>
          <div>
            <Label>Username</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="input-username" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-password" />
          </div>
          <div className="col-span-2">
            <Label>Full Name</Label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="input-fullname" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="select-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Active</Label>
            <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Active</SelectItem>
                <SelectItem value="no">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-cyan-700 hover:bg-cyan-600" data-testid="submit-create-login">
            {busy && <Loader2 className="animate-spin mr-1" size={14} />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginRow({ login, onUpdated, canDelete }) {
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");

  const updateRole = async (role) => {
    setBusy(true);
    try {
      await admin.updateLogin(login.username, { role });
      toast.success(`Role updated to ${role}`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await admin.updateLogin(login.username, { active: !login.active });
      toast.success(login.active ? "Deactivated" : "Activated");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const resetPw = async () => {
    if (!newPw) { toast.error("Enter a new password"); return; }
    setBusy(true);
    try {
      await admin.updateLogin(login.username, { password: newPw });
      toast.success("Password updated");
      setPwOpen(false); setNewPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await admin.deleteLogin(login.username);
      toast.success("Login deleted");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-slate-100" data-testid={`login-row-${login.username}`}>
      <td className="px-3 py-2 font-semibold mono">{login.username}</td>
      <td className="px-3 py-2">{login.full_name || "—"}</td>
      <td className="px-3 py-2 mono text-slate-600">{login.emp_no}</td>
      <td className="px-3 py-2">
        <Select value={login.role} onValueChange={updateRole} disabled={busy}>
          <SelectTrigger className="h-8 w-[140px]" data-testid={`role-${login.username}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2">
        <Button
          variant="outline" size="sm" onClick={toggleActive} disabled={busy}
          className={`h-7 text-[11px] ${login.active ? "border-emerald-300 text-emerald-800" : "border-slate-300 text-slate-500"}`}
          data-testid={`toggle-active-${login.username}`}
        >
          {login.active ? "Active" : "Inactive"}
        </Button>
      </td>
      <td className="px-3 py-2 text-right">
        <Dialog open={pwOpen} onOpenChange={setPwOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2" data-testid={`reset-pw-${login.username}`}>
              <KeyRound size={13} />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset password for {login.username}</DialogTitle>
              <DialogDescription>This overwrites the password in <code>ULogin.Pass</code>.</DialogDescription>
            </DialogHeader>
            <Input
              type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password" data-testid="new-pw-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
              <Button onClick={resetPw} disabled={busy} className="bg-cyan-700 hover:bg-cyan-600" data-testid="confirm-reset-pw">
                Update password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-${login.username}`}>
                <Trash2 size={13} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete login {login.username}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the row from <code>ULogin</code> in MS SQL. They will no longer be able to sign in.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-${login.username}`}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </td>
    </tr>
  );
}
